import { useEffect, useRef, useCallback } from 'react'

interface UseInfiniteScrollOptions {
  onLoadMore: () => void
  isLoading?: boolean
  hasMore?: boolean
  threshold?: number
}

interface UseInfiniteScrollReturn {
  containerRef: (node: HTMLDivElement | null) => void
  sentinelRef: (node: HTMLDivElement | null) => void
}

export const useInfiniteScroll = ({
  onLoadMore,
  isLoading = false,
  hasMore = true,
  threshold = 100,
}: UseInfiniteScrollOptions): UseInfiniteScrollReturn => {
  const containerNodeRef = useRef<HTMLDivElement | null>(null)
  const sentinelNodeRef = useRef<HTMLDivElement | null>(null)
  const observerRef = useRef<IntersectionObserver | null>(null)
  const onLoadMoreRef = useRef(onLoadMore)
  const isLoadingRef = useRef(isLoading)
  const hasMoreRef = useRef(hasMore)
  const isLoadingLocalRef = useRef(false)

  // Keep refs up to date
  useEffect(() => {
    onLoadMoreRef.current = onLoadMore
  }, [onLoadMore])

  useEffect(() => {
    isLoadingRef.current = isLoading
    if (!isLoading) {
      isLoadingLocalRef.current = false
    }
  }, [isLoading])

  useEffect(() => {
    hasMoreRef.current = hasMore
  }, [hasMore])

  // Memoize setupObserver to prevent unnecessary recreations
  const setupObserver = useCallback(() => {
    const sentinel = sentinelNodeRef.current
    const container = containerNodeRef.current

    if (!sentinel || !container) {
      return
    }

    // Clean up existing observer
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    // Create new observer
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]

        // Uses refs for isLoading, hasMore, onLoadMore so observer doesn't need
        // to be recreated when these values change
        if (
          entry.isIntersecting &&
          !isLoadingRef.current &&
          !isLoadingLocalRef.current &&
          hasMoreRef.current
        ) {
          isLoadingLocalRef.current = true
          onLoadMoreRef.current()
        }
      },
      {
        root: container,
        rootMargin: `${threshold}px`,
        threshold: 0,
      }
    )

    observer.observe(sentinel)
    observerRef.current = observer
  }, [threshold])

  // Callback ref for container
  const containerRef = useCallback(
    (node: HTMLDivElement | null) => {
      containerNodeRef.current = node
      if (node) {
        setupObserver()
      }
    },
    [setupObserver]
  )

  // Callback ref for sentinel
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      sentinelNodeRef.current = node
      if (node) {
        setupObserver()
      }
    },
    [setupObserver]
  )

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [])

  return { containerRef, sentinelRef }
}
